/**
 * useResourceDrag - Hook for sidebar item drag & drop functionality
 *
 * Encapsulates all drag & drop logic for sidebar items including:
 * - Drag start with visual preview
 * - Drop mode detection (reorder vs group)
 * - Drag over/leave/drop handlers
 *
 * Used by SidebarItem to provide consistent drag & drop across all resource types.
 */

import { useCallback, useRef, useState, useEffect } from "react";
import { ResourceType, GroupableResourceType } from "@/models/resource-types";
import {
  setResourceDragData,
  getResourceDragPayload,
  hasResourceDragData,
  DropMode,
  RESOURCE_DRAG_TYPE,
} from "@/components/layout/arc-sidebar/resource-item-wrapper";
import { setDragPayload, DragPayload } from "@/utils/drag-types";

// ============================================================================
// Types
// ============================================================================

export interface UseResourceDragOptions {
  /** Unique identifier of the resource */
  resourceId: string;
  /** Type of resource (tracker, enrichment, alert, etc.) */
  resourceType: ResourceType;
  /** Display name for drag preview */
  displayName: string;
  /** Optional color for drag preview border */
  color?: string;
  /** Whether drag is enabled */
  enabled?: boolean;
  /** Callback when another resource is dropped on this one to create a group */
  onDropToCreateGroup?: (draggedId: string, targetId: string) => void;
  /** Optional drag payload for split view (if not provided, only resource drag is set) */
  dragPayload?: DragPayload;
}

export interface UseResourceDragReturn {
  /** Ref to attach to the draggable element */
  itemRef: React.RefObject<HTMLDivElement | null>;
  /** Props to spread on the draggable element */
  dragProps: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  /** Whether this item is currently being dragged */
  isDragging: boolean;
  /** Whether something is being dragged over this item */
  isDragOver: boolean;
  /** Current drop mode (reorder or group) */
  dropMode: DropMode;
  /** Whether the drop mode is "group" (for visual indicator) */
  isGroupMode: boolean;
  /** Whether the drop mode is "reorder" (for visual indicator) */
  isReorderMode: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useResourceDrag({
  resourceId,
  resourceType,
  displayName,
  color,
  enabled = true,
  onDropToCreateGroup,
  dragPayload,
}: UseResourceDragOptions): UseResourceDragReturn {
  const itemRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropMode, setDropMode] = useState<DropMode>("reorder");

  // Track if we're currently dragging over this specific element
  const dragOverCountRef = useRef(0);

  // Listen for any drop events globally during mount
  useEffect(() => {
    const handleWindowDragOver = (e: DragEvent) => {
      // Prevent default at window level to allow drops anywhere
      e.preventDefault();
    };

    window.addEventListener("dragover", handleWindowDragOver, false);
    return () => {
      window.removeEventListener("dragover", handleWindowDragOver, false);
    };
  }, []);

  /**
   * Determine drop mode based on cursor position within the element.
   * Center zone (30%-70% of height) = group mode, edges = reorder mode.
   */
  const getDropModeFromEvent = useCallback((e: React.DragEvent): DropMode => {
    if (!itemRef.current) return "reorder";
    const rect = itemRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const relativeY = y / rect.height;
    // Center zone (30%-70%) triggers group mode
    const mode = relativeY > 0.3 && relativeY < 0.7 ? "group" : "reorder";
    return mode;
  }, []);

  /**
   * Create a visual drag preview (scaled clone of the element).
   */
  const createDragPreview = useCallback(
    (e: React.DragEvent) => {
      if (!itemRef.current || !e.dataTransfer.setDragImage) return;

      const rect = itemRef.current.getBoundingClientRect();
      // Scale down to a small preview (max 200px width)
      const maxWidth = 200;
      const scale = Math.min(1, maxWidth / rect.width);
      const scaledWidth = rect.width * scale;
      const scaledHeight = rect.height * scale;

      // Create a wrapper that has the final scaled size
      const wrapper = document.createElement("div");
      wrapper.style.position = "absolute";
      wrapper.style.top = "-9999px";
      wrapper.style.left = "-9999px";
      wrapper.style.width = `${scaledWidth}px`;
      wrapper.style.height = `${scaledHeight}px`;
      wrapper.style.overflow = "hidden";
      wrapper.style.borderRadius = "8px";
      wrapper.style.boxShadow = "0 8px 24px rgba(0,0,0,0.25)";
      wrapper.style.border = `2px solid ${color || "var(--arc-accent)"}`;

      // Create a scaled clone inside the wrapper
      const clone = itemRef.current.cloneNode(true) as HTMLElement;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.transform = `scale(${scale})`;
      clone.style.transformOrigin = "top left";
      clone.style.opacity = "0.95";
      clone.style.background = "var(--arc-sidebar-bg)";

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      // Set the drag image with offset to center it
      e.dataTransfer.setDragImage(wrapper, scaledWidth / 2, scaledHeight / 2);

      // Remove the wrapper after a short delay
      requestAnimationFrame(() => {
        document.body.removeChild(wrapper);
      });
    },
    [color],
  );

  /**
   * Handle drag start.
   */
  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (!enabled) {
        e.preventDefault();
        return;
      }

      setIsDragging(true);
      e.dataTransfer.effectAllowed = "copyMove";

      // Set resource drag data (for grouping functionality)
      // Only set drag data for groupable resource types
      if (resourceType !== "widget" && resourceType !== "conversation" && resourceType !== "investigation" && resourceType !== "report") {
        setResourceDragData(e, resourceId, resourceType as GroupableResourceType, displayName);
      }

      // Set split view drag payload if provided
      if (dragPayload) {
        setDragPayload(e, dragPayload);
      }

      // Create visual drag preview
      createDragPreview(e);
    },
    [
      enabled,
      resourceId,
      resourceType,
      displayName,
      dragPayload,
      createDragPreview,
    ],
  );

  /**
   * Handle drag over.
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      // Only accept resource drags (for grouping)
      const hasResourceData = hasResourceDragData(e);
      if (!hasResourceData) {
        return;
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      const mode = getDropModeFromEvent(e);
      setIsDragOver(true);
      setDropMode(mode);
    },
    [getDropModeFromEvent],
  );

  /**
   * Handle drag leave.
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setDropMode("reorder");
  }, []);

  /**
   * Handle drop.
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      setDropMode("reorder");
      setIsDragging(false);

      // Get the dragged resource
      const payload = getResourceDragPayload(e);
      if (!payload) return;

      // Don't allow dropping on itself
      if (payload.resourceId === resourceId) return;

      // Only accept same resource type drops
      if (payload.resourceType !== resourceType) {
        return;
      }

      // Determine if we're in group mode
      const mode = getDropModeFromEvent(e);

      if (mode === "group" && onDropToCreateGroup) {
        // Create a new group containing both resources
        onDropToCreateGroup(payload.resourceId, resourceId);
      }
    },
    [resourceId, resourceType, getDropModeFromEvent, onDropToCreateGroup],
  );

  // Computed values
  const isGroupMode = isDragOver && dropMode === "group";
  const isReorderMode = isDragOver && dropMode === "reorder";

  // Use native DOM events as a fallback
  useEffect(() => {
    const element = itemRef.current;
    if (!element || !enabled) return;

    const handleNativeDragOver = (e: DragEvent) => {
      // Always prevent default on dragover to allow drop
      e.preventDefault();
      e.dataTransfer!.dropEffect = "move";
    };

    const handleNativeDrop = (e: DragEvent) => {
      e.preventDefault();

      try {
        const textData = e.dataTransfer!.getData("text/plain");
        if (!textData) return;

        let payload;
        try {
          payload = JSON.parse(textData);
        } catch {
          return;
        }

        if (
          payload.type === "resource" &&
          payload.resourceId !== resourceId &&
          payload.resourceType === resourceType &&
          onDropToCreateGroup
        ) {
          e.stopPropagation();
          onDropToCreateGroup(payload.resourceId, resourceId);
        }
      } catch {
        // Silently ignore errors
      }
    };

    element.addEventListener("dragover", handleNativeDragOver);
    element.addEventListener("drop", handleNativeDrop);

    return () => {
      element.removeEventListener("dragover", handleNativeDragOver);
      element.removeEventListener("drop", handleNativeDrop);
    };
  }, [resourceId, enabled, resourceType, onDropToCreateGroup]);

  // Handle drag end
  const handleDragEnd = useCallback((e: React.DragEvent) => {
    setIsDragging(false);
    setIsDragOver(false);
    setDropMode("reorder");
  }, []);

  // Override handlers to ensure drop works
  const enhancedHandleDragOver = useCallback(
    (e: React.DragEvent) => {
      // Always prevent default to allow drop
      e.preventDefault();
      // Call our handler
      handleDragOver(e);
    },
    [handleDragOver],
  );

  const enhancedHandleDrop = useCallback(
    (e: React.DragEvent) => {
      // Always prevent default
      e.preventDefault();
      // Call our handler
      handleDrop(e);
    },
    [handleDrop],
  );

  return {
    itemRef,
    dragProps: {
      draggable: enabled,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      onDragOver: enhancedHandleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: enhancedHandleDrop,
    },
    isDragging,
    isDragOver,
    dropMode,
    isGroupMode,
    isReorderMode,
  };
}

export default useResourceDrag;
